import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

/**
 * Client-side paging for an already-bounded, already-sorted list.
 *
 * The validator tables come back capped at 200 rows and ordered worst-first, so the rows that
 * matter are always on the first page and there is nothing to gain from a round trip per page.
 * Paging here keeps the DOM small — a 200-row table is unusable on a phone and slow to lay out
 * anywhere.
 */
const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};

  ${media.sm} {
    flex-wrap: wrap;
  }
`;

const PageButton = styled.button`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  color: ${theme.colors.text};
  padding: 3px 9px;
  font-size: ${theme.fontSize.xs};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: ${theme.colors.bgHover};
    border-color: ${theme.colors.textMuted};
  }
`;

const Range = styled.span`
  margin-right: auto;
  font-family: monospace;
`;

/**
 * Slice a list into pages, resetting to the first page whenever the list identity changes —
 * otherwise switching the health window while on page 4 leaves you looking at an empty table.
 */
export function usePagedRows<T>(rows: T[], pageSize: number) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));

  useEffect(() => {
    setPage(0);
  }, [rows]);

  // Guards against a shrinking list leaving `page` past the end before the reset lands.
  const safePage = Math.min(page, pageCount - 1);
  const visible = useMemo(
    () => rows.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [rows, safePage, pageSize],
  );

  return {
    visible,
    page: safePage,
    pageCount,
    setPage,
    from: rows.length === 0 ? 0 : safePage * pageSize + 1,
    to: Math.min((safePage + 1) * pageSize, rows.length),
    total: rows.length,
  };
}

export interface PaginationProps {
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
  onPage: (page: number) => void;
  /** Appended to the row range, e.g. "capped at 200". */
  note?: string;
}

export function Pagination({ page, pageCount, from, to, total, onPage, note }: PaginationProps) {
  if (total === 0) return null;
  return (
    <Bar>
      <Range>
        {from}–{to} of {total}
        {note ? ` · ${note}` : ""}
      </Range>
      <PageButton onClick={() => onPage(0)} disabled={page === 0} title="First page">
        «
      </PageButton>
      <PageButton onClick={() => onPage(page - 1)} disabled={page === 0}>
        Prev
      </PageButton>
      <span>
        {page + 1} / {pageCount}
      </span>
      <PageButton onClick={() => onPage(page + 1)} disabled={page >= pageCount - 1}>
        Next
      </PageButton>
      <PageButton
        onClick={() => onPage(pageCount - 1)}
        disabled={page >= pageCount - 1}
        title="Last page"
      >
        »
      </PageButton>
    </Bar>
  );
}
